import { useCallback } from 'react';

const SCOREBOARD_STATE_KEY = 'volleyball_scoreboard_state';
const AUTO_SAVE_INTERVAL = 2000; // Save every 2 seconds when there's activity

export const useScoreboardPersistence = (user, isGuestMode) => {
  // Generate a unique key for this user's scoreboard state
  const getStorageKey = useCallback(() => {
    if (isGuestMode) {
      return `${SCOREBOARD_STATE_KEY}_guest`;
    }
    return `${SCOREBOARD_STATE_KEY}_${user?.username || user?._id || 'unknown'}`;
  }, [user, isGuestMode]);

  // Save scoreboard state to localStorage
  const saveScoreboardState = useCallback((state) => {
    try {
      const stateToSave = {
        ...state,
        lastSaved: new Date().toISOString(),
        version: '1.0', // For future compatibility
        userAgent: navigator.userAgent.substring(0, 100), // First 100 chars for debugging
        isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      };
      
      localStorage.setItem(getStorageKey(), JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Error saving scoreboard state:', error);
    }
  }, [getStorageKey]);

  // Load scoreboard state from localStorage
  const loadScoreboardState = useCallback(() => {
    try {
      const savedState = localStorage.getItem(getStorageKey());
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        
        // Check if state is recent (within last 24 hours)
        const lastSaved = new Date(parsedState.lastSaved);
        const now = new Date();
        const hoursAgo = (now - lastSaved) / (1000 * 60 * 60);
        
        if (hoursAgo > 24) {
          clearScoreboardState();
          return null;
        }

        return parsedState;
      }
    } catch (error) {
      console.error('Error loading scoreboard state:', error);
    }
    return null;
  }, [getStorageKey]);

  // Clear scoreboard state from localStorage
  const clearScoreboardState = useCallback(() => {
    try {
      localStorage.removeItem(getStorageKey());
    } catch (error) {
      console.error('Error clearing scoreboard state:', error);
    }
  }, [getStorageKey]);

  // Check if there's any meaningful state to persist
  const hasActiveState = useCallback((state) => {
    if (!state) {
      return false;
    }

    return !!(
      state.teamA ||
      state.teamB ||
      state.scoreA > 0 ||
      state.scoreB > 0 ||
      state.time > 0 ||
      state.note ||
      (state.scoringOrder && state.scoringOrder.length > 0) ||
      (state.timeoutsA && state.timeoutsA.some(t => t)) ||
      (state.timeoutsB && state.timeoutsB.some(t => t))
    );
  }, []);

  // Auto-save functionality with debouncing
  const createAutoSaver = useCallback((stateSetter) => {
    let saveTimeout;
    
    return (newState) => {
      // Clear existing timeout
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
      
      // Set new timeout to save state
      saveTimeout = setTimeout(() => {
        if (hasActiveState(newState)) {
          saveScoreboardState(newState);
        } else {
          // Clear state if it's empty/inactive
          clearScoreboardState();
        }
      }, AUTO_SAVE_INTERVAL);
    };
  }, [hasActiveState, saveScoreboardState, clearScoreboardState]);

  // Prepare state object for saving
  const prepareStateForSaving = useCallback((stateGetters) => {
    return {
      teamA: stateGetters.teamA,
      teamB: stateGetters.teamB,
      scoreA: stateGetters.scoreA,
      scoreB: stateGetters.scoreB,
      time: stateGetters.time,
      isRunning: stateGetters.isRunning,
      clockBaseSeconds: stateGetters.clockBaseSeconds,
      clockStartedAt: stateGetters.clockStartedAt,
      timeoutClock: stateGetters.timeoutClock || null,
      note: stateGetters.note,
      scoringOrder: stateGetters.scoringOrder,
      servingTeam: stateGetters.servingTeam,
      timeoutsA: stateGetters.timeoutsA,
      timeoutsB: stateGetters.timeoutsB,
      colorsSwapped: stateGetters.colorsSwapped,
      currentMatchGroup: stateGetters.currentMatchGroup,
      currentSetNumber: stateGetters.currentSetNumber,
      isPublic: stateGetters.isPublic,
      liveShare: stateGetters.liveShare || null,
      editingMatch: stateGetters.editingMatch ? {
        _id: stateGetters.editingMatch._id,
        teamA: stateGetters.editingMatch.teamA,
        teamB: stateGetters.editingMatch.teamB,
        scoreA: stateGetters.editingMatch.scoreA,
        scoreB: stateGetters.editingMatch.scoreB,
        note: stateGetters.editingMatch.note,
        time: stateGetters.editingMatch.time,
        scoringOrder: stateGetters.editingMatch.scoringOrder,
        timeoutsA: stateGetters.editingMatch.timeoutsA,
        timeoutsB: stateGetters.editingMatch.timeoutsB,
        matchGroup: stateGetters.editingMatch.matchGroup,
        setNumber: stateGetters.editingMatch.setNumber,
        isPublic: stateGetters.editingMatch.isPublic
      } : null
    };
  }, []);

  // Get restoration status
  const getRestorationInfo = useCallback(() => {
    const savedState = loadScoreboardState();

    if (!savedState || !hasActiveState(savedState)) {
      return null;
    }

    return {
      hasState: true,
      lastSaved: savedState.lastSaved,
      teamA: savedState.teamA,
      teamB: savedState.teamB,
      scoreA: savedState.scoreA,
      scoreB: savedState.scoreB,
      time: savedState.time,
      isEditing: !!savedState.editingMatch
    };
  }, [loadScoreboardState, hasActiveState]);

  return {
    saveScoreboardState,
    loadScoreboardState,
    clearScoreboardState,
    hasActiveState,
    createAutoSaver,
    prepareStateForSaving,
    getRestorationInfo
  };
};
